import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Utensils, Accessibility, User, Users, RefreshCw, Calendar, Clock, MapPin, Globe, BarChart3, TrendingUp, Eye, Building2, Briefcase } from "lucide-react";
import PhygitalIcon from "@/components/PhygitalIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatTimestamp = (date) => {
  const now = new Date(date);
  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const tzAbbr = now.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
  return `${dateStr} at ${timeStr} ${tzAbbr}`;
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/')) {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${baseUrl}${path}`;
  }
  return `${API}${path.startsWith('/') ? '' : '/'}${path}`;
};

const formatTime12Hour = (time) => {
  if (!time) return "";
  let hours, minutes;
  if (time.includes(':')) {
    [hours, minutes] = time.split(':').map(Number);
  } else {
    return time;
  }
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${String(minutes).padStart(2, '0')} ${ampm}`;
};

// Color palette for charts
const CHART_COLORS = [
  { bg: "bg-indigo-500", hex: "#6366f1", light: "bg-indigo-100", text: "text-indigo-700" },
  { bg: "bg-violet-500", hex: "#8b5cf6", light: "bg-violet-100", text: "text-violet-700" },
  { bg: "bg-blue-500", hex: "#3b82f6", light: "bg-blue-100", text: "text-blue-700" },
  { bg: "bg-emerald-500", hex: "#10b981", light: "bg-emerald-100", text: "text-emerald-700" },
  { bg: "bg-amber-500", hex: "#f59e0b", light: "bg-amber-100", text: "text-amber-700" },
  { bg: "bg-rose-500", hex: "#f43f5e", light: "bg-rose-100", text: "text-rose-700" },
  { bg: "bg-cyan-500", hex: "#06b6d4", light: "bg-cyan-100", text: "text-cyan-700" },
  { bg: "bg-pink-500", hex: "#ec4899", light: "bg-pink-100", text: "text-pink-700" },
];

// Horizontal bar chart component
const HorizontalBarChart = ({ data, total, title, icon: ChartIcon, colorIdx = 0 }) => {
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data);
  const maxVal = Math.max(...entries.map(([, v]) => v));

  return (
    <Card className="overflow-hidden" data-testid={`chart-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          {ChartIcon && <ChartIcon className="w-4.5 h-4.5 text-gray-500" />}
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <span className="ml-auto text-xs text-gray-400">{entries.length} categories</span>
        </div>
        <div className="space-y-3">
          {entries.map(([label, count], i) => {
            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
            const barPct = maxVal > 0 ? (count / maxVal) * 100 : 0;
            const color = CHART_COLORS[(colorIdx + i) % CHART_COLORS.length];
            return (
              <div key={label} data-testid={`bar-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700 truncate max-w-[60%]">{label}</span>
                  <span className="text-xs font-medium text-gray-500 tabular-nums">{count} ({pct}%)</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color.bg} rounded-full transition-all duration-700 ease-out`}
                    style={{ width: `${barPct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Donut chart component for pie-style visualization
const DonutChartCard = ({ data, total, title, icon: ChartIcon }) => {
  if (!data || Object.keys(data).length === 0) return null;
  const entries = Object.entries(data);
  const chartTotal = entries.reduce((s, [, v]) => s + v, 0);
  
  const size = 140;
  const strokeWidth = 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <Card className="overflow-hidden" data-testid={`donut-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          {ChartIcon && <ChartIcon className="w-4.5 h-4.5 text-gray-500" />}
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        <div className="flex items-center gap-6">
          <div className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="transform -rotate-90">
              <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#f3f4f6" strokeWidth={strokeWidth} />
              {entries.map(([label, count], idx) => {
                const pct = count / chartTotal;
                const dashArray = `${pct * circumference} ${circumference}`;
                const dashOffset = -offset * circumference;
                offset += pct;
                const color = CHART_COLORS[idx % CHART_COLORS.length];
                return (
                  <circle
                    key={label}
                    cx={size/2} cy={size/2} r={radius}
                    fill="none" stroke={color.hex}
                    strokeWidth={strokeWidth}
                    strokeDasharray={dashArray}
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                    className="transition-all duration-700"
                  />
                );
              })}
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-xl font-bold text-gray-900">{chartTotal}</p>
                <p className="text-[10px] text-gray-400">responses</p>
              </div>
            </div>
          </div>
          <div className="flex-1 space-y-1.5 min-w-0">
            {entries.map(([label, count], idx) => {
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              const color = CHART_COLORS[idx % CHART_COLORS.length];
              return (
                <div key={label} className="flex items-center gap-2 text-sm">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${color.bg}`} />
                  <span className="text-gray-700 truncate flex-1">{label}</span>
                  <span className="text-xs text-gray-500 tabular-nums shrink-0">{count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function EventSharePage() {
  const { eventId, shareType } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [aggregated, setAggregated] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const isDietary = shareType === "dietary";
  const isAccessibility = shareType === "accessibility";
  const isInterests = shareType === "interests";
  const isInstructor = shareType === "instructor";

  useEffect(() => {
    fetchShareData();
    const interval = setInterval(() => {
      fetchShareData();
      setLastRefresh(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [eventId, shareType]);

  const fetchShareData = async () => {
    try {
      const res = await fetch(`${API}/events/${eventId}/share/${shareType}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 403) throw new Error("disabled");
        throw new Error(data.detail || "Failed to load data");
      }
      const data = await res.json();
      setEvent(data.event);
      setTotalCount(data.total_count || 0);
      if (data.aggregated) {
        setAggregated(data.aggregated);
      } else {
        setGuests(data.guests || []);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching share data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600" />
          <p className="mt-2 text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (error === "disabled") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Link Unavailable</h2>
            <p className="text-gray-500 text-sm">The host has disabled sharing for this event.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Icon = isDietary ? Utensils : isAccessibility ? Accessibility : isInstructor ? BarChart3 : User;
  const title = isDietary ? "Dietary Requirements" : isAccessibility ? "Accessibility Needs" : isInstructor ? "Audience Insights" : "Interests & Experience";

  const eventBackground = event?.attendee_card_background || event?.background_theme_url || event?.welcome_background;
  const eventLogo = event?.cover_image || event?.logo_image;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="event-share-page">
      {/* Header */}
      <header
        className="text-white py-8 relative"
        style={{
          background: eventBackground
            ? `url(${getImageUrl(eventBackground)}) center/cover`
            : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <div className="flex items-center gap-4 mb-4">
            {eventLogo && (
              <div
                className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center shadow-lg"
                style={{ backgroundColor: event?.logo_background_color || "#000" }}
              >
                <img src={getImageUrl(eventLogo)} alt="" className="w-12 h-12 object-contain" />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold drop-shadow-md">{event?.name || "Event"}</h1>
              {(() => {
                const hostNames = [];
                if (event?.org_name) hostNames.push(event.org_name);
                if (event?.team) {
                  event.team.forEach(member => {
                    if ((member.label === "host" || member.role === "host" || member.role === "owner") && member.org_name && !hostNames.includes(member.org_name)) {
                      hostNames.push(member.org_name);
                    }
                  });
                }
                if (event?.host_orgs) {
                  event.host_orgs.forEach(org => {
                    if (org.name && !hostNames.includes(org.name)) hostNames.push(org.name);
                  });
                }
                return hostNames.length > 0 ? (
                  <p className="text-sm opacity-90 drop-shadow">by {hostNames.join(' & ')}</p>
                ) : null;
              })()}
              <div className="flex items-center flex-wrap gap-3 text-sm opacity-90 mt-1 drop-shadow">
                {event?.date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {(() => {
                      const [year, month, day] = event.date.split('T')[0].split('-');
                      const localDate = new Date(year, month - 1, day);
                      return localDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                    })()}
                    {event?.start_time && (
                      <>
                        {' '}
                        <Clock className="w-4 h-4" />
                        {formatTime12Hour(event.start_time)}
                        {event?.timezone_abbr && ` ${event.timezone_abbr}`}
                      </>
                    )}
                  </span>
                )}
                {event?.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 drop-shadow">
            <Icon className="w-6 h-6" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <p className="mt-1 opacity-90 drop-shadow">
            {isInstructor
              ? `${totalCount} attendee${totalCount !== 1 ? 's' : ''} registered`
              : `${guests.length} guest${guests.length !== 1 ? 's' : ''} shared their ${isDietary ? 'dietary' : isAccessibility ? 'accessibility' : 'interests'} information`
            }
          </p>
        </div>
      </header>

      {/* Live indicator */}
      <div className="bg-white border-b py-2">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-green-600">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            Live updates enabled
          </div>
          <div className="flex items-center gap-2 text-gray-500">
            <Globe className="w-3.5 h-3.5" />
            <span>Updated: {formatTimestamp(lastRefresh)}</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* INSTRUCTOR VIEW: Aggregate charts */}
        {isInstructor && aggregated && (
          totalCount === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-indigo-300" />
                <p className="text-gray-500">No audience data yet.</p>
                <p className="text-sm text-gray-400 mt-2">This page will update automatically as guests RSVP.</p>
              </CardContent>
            </Card>
          ) : (() => {
            // Chart config with labels and icons
            const chartConfig = [
              { key: "interests", label: "Interests", icon: TrendingUp, type: "bar" },
              { key: "audience_type", label: "Audience Type", icon: Users, type: "donut" },
              { key: "city", label: "Location", icon: MapPin, type: "donut" },
              { key: "stage", label: "Stage", icon: BarChart3, type: "donut" },
              { key: "learning_preferences", label: "Learning Preferences", icon: Eye, type: "bar" },
              { key: "vibe", label: "Vibe", icon: Globe, type: "donut" },
              { key: "accessibility_needs", label: "Accessibility Needs", icon: Accessibility, type: "bar" },
              { key: "dietary_restrictions", label: "Dietary Restrictions", icon: Utensils, type: "donut" },
            ];
            // Only show charts that have data
            const activeCharts = chartConfig.filter(c => aggregated[c.key] && Object.keys(aggregated[c.key]).length > 0);
            // Summary stats from active charts
            const summaryStats = [
              { label: "Registered", value: totalCount, color: "text-gray-900" },
              ...activeCharts.slice(0, 3).map(c => ({
                label: c.label,
                value: Object.keys(aggregated[c.key]).length,
                color: ["text-indigo-600", "text-violet-600", "text-amber-600"][activeCharts.indexOf(c) % 3]
              }))
            ];
            
            return (
              <div className="space-y-5" data-testid="instructor-aggregate-charts">
                {/* Summary stats */}
                <div className={`grid grid-cols-2 sm:grid-cols-${Math.min(summaryStats.length, 4)} gap-3`}>
                  {summaryStats.map((s, i) => (
                    <Card key={i}>
                      <CardContent className="p-4 text-center">
                        <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Dynamic charts - mixed bar and donut */}
                {activeCharts.map((chart, idx) => (
                  chart.type === "donut" ? (
                    <DonutChartCard
                      key={chart.key}
                      data={aggregated[chart.key]}
                      total={totalCount}
                      title={chart.label}
                      icon={chart.icon}
                    />
                  ) : (
                    <HorizontalBarChart
                      key={chart.key}
                      data={aggregated[chart.key]}
                      total={totalCount}
                      title={chart.label}
                      icon={chart.icon}
                      colorIdx={idx}
                    />
                  )
                ))}

                {activeCharts.length === 0 && (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <p className="text-gray-500">No insight data available yet.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Privacy notice */}
                <p className="text-xs text-gray-400 text-center pt-2">
                  Individual attendee data is not shared. Charts show aggregate totals only.
                </p>
              </div>
            );
          })()
        )}

        {/* NON-INSTRUCTOR VIEWS: Per-guest cards (dietary, accessibility, interests) */}
        {!isInstructor && (
          guests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Icon className="w-12 h-12 mx-auto mb-4" style={{ color: isDietary ? '#fdba74' : isAccessibility ? '#93c5fd' : '#f9a8d4' }} />
                <p className="text-gray-500">No guests have shared their {isDietary ? 'dietary' : isAccessibility ? 'accessibility' : 'interests'} information yet.</p>
                <p className="text-sm text-gray-400 mt-2">This page will update automatically as guests RSVP.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {guests.map((guest, idx) => (
                <Card key={guest.user_id || idx} className="overflow-hidden" data-testid={`guest-card-${idx}`}>
                  <CardContent className="p-0">
                    <div className="flex">
                      <div className="flex-1 p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium">{guest.name || "Guest"}</p>
                            <p className="text-xs text-gray-500">Shared {formatRelativeTime(guest.shared_at || guest.created_at)}</p>
                          </div>
                        </div>

                        {isDietary && (
                          <>
                            {guest.dietary_restrictions?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Dietary Restrictions</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.dietary_restrictions.map((d, i) => (
                                    <Badge key={i} className="bg-orange-100 text-orange-700">{d}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.allergies?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Allergies</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.allergies.map((a, i) => (
                                    <Badge key={i} variant="destructive">{a}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.dietary_notes && (
                              <div>
                                <p className="text-sm font-medium text-gray-500 mb-1">Notes</p>
                                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{guest.dietary_notes}</p>
                              </div>
                            )}
                          </>
                        )}

                        {isAccessibility && (
                          <>
                            {guest.accessibility_needs?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Accessibility Needs</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.accessibility_needs.map((a, i) => (
                                    <Badge key={i} className="bg-blue-100 text-blue-700">{a.replace(/_/g, ' ')}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.invisible_disabilities?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Invisible Disabilities</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.invisible_disabilities.map((d, i) => (
                                    <Badge key={i} className="bg-purple-100 text-purple-700">{d.replace(/_/g, ' ')}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.mobility_needs && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Mobility</p>
                                <p className="text-sm">{guest.mobility_needs}</p>
                              </div>
                            )}
                            {guest.anything_else_to_share && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Additional Info</p>
                                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{guest.anything_else_to_share}</p>
                              </div>
                            )}
                            {guest.accessibility_notes && (
                              <div>
                                <p className="text-sm font-medium text-gray-500 mb-1">Notes</p>
                                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{guest.accessibility_notes}</p>
                              </div>
                            )}
                          </>
                        )}

                        {isInterests && (
                          <>
                            {guest.interests?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Interests</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.interests.map((interest, i) => (
                                    <Badge key={i} className="bg-pink-100 text-pink-700">{interest}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.audience_type?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Audience Type</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.audience_type.map((type, i) => (
                                    <Badge key={i} className="bg-violet-100 text-violet-700">{type.replace(/_/g, ' ')}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.learning_preferences?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Learning Preferences</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.learning_preferences.map((pref, i) => (
                                    <Badge key={i} className="bg-indigo-100 text-indigo-700">{pref.replace(/_/g, ' ')}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {(Array.isArray(guest.vibe) ? guest.vibe.length > 0 : guest.vibe) && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Vibe</p>
                                <div className="flex flex-wrap gap-1">
                                  {Array.isArray(guest.vibe) ? (
                                    guest.vibe.map((v, i) => (
                                      <Badge key={i} className="bg-purple-100 text-purple-700">{v.replace(/_/g, ' ')}</Badge>
                                    ))
                                  ) : (
                                    <Badge className="bg-purple-100 text-purple-700">{String(guest.vibe).replace(/_/g, ' ')}</Badge>
                                  )}
                                </div>
                              </div>
                            )}
                            {guest.experience_apps?.length > 0 && (
                              <div className="mb-3">
                                <p className="text-sm font-medium text-gray-500 mb-1">Experience Apps</p>
                                <div className="flex flex-wrap gap-1">
                                  {guest.experience_apps.map((app, i) => (
                                    <Badge key={i} className="bg-emerald-100 text-emerald-700">{app}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                            {guest.additional_notes && (
                              <div>
                                <p className="text-sm font-medium text-gray-500 mb-1">Notes</p>
                                <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{guest.additional_notes}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-gray-100 bg-white">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-2 text-sm text-gray-500">
          <PhygitalIcon className="w-4 h-4" />
          <span>Shared via Phygital</span>
        </div>
      </footer>
    </div>
  );
}
